import React, { FC, useLayoutEffect } from "react";
import { observer } from "mobx-react";
import {
  AddressModel,
  CheckoutPaymentPageModel,
  CheckOutPayPageFormFields,
  CountryWithStateModel,
  CustomerFacingCheckOutItemModel,
  getPurchaseItemsPriceDetails,
  MerchantAccountWithPaySettingsModel,
  PurchaseCheckOutAddOnDetails,
  PurchaseItemsPriceModel,
  PurchaseMerchantAccountSettingsModel,
  PurchasePaymentModel,
  SellerModel,
  StateModel,
} from "../models";
import { ICheckOutCombinedColors } from "../interfaces";
import {
  CheckOutButtonStyle,
  CheckOutPayButtonCaption,
  CheckOutPayFields,
  CheckOutPromoCodeSelectionType,
} from "../enums";
import {
  CustomButton,
  CustomLabel,
  FlexContainer,
  Input,
} from "@mindmemobile/mm-shared-global";
import CountryPhoneDropdown from "./CountryPhoneDropdown";
import AddressFormComponent from "./AddressFormComponent";

type IProps = {
  payDetails: CheckoutPaymentPageModel;
  paymentSectionData: PurchasePaymentModel;
  handleDataChange: (field: keyof PurchasePaymentModel, value: any) => void;
  countriesWithState: CountryWithStateModel[];
  onPayClick: () => void;
  color?: ICheckOutCombinedColors;
  merchantAccount: MerchantAccountWithPaySettingsModel;
  addons: PurchaseCheckOutAddOnDetails[];
  items: CustomerFacingCheckOutItemModel[];
  action?: string;
  hasMultiplePaymentMethods?: boolean;
  isPaymentInProgress?: boolean;
  seller: SellerModel;
  showSurchargeCheckbox?: boolean;
  includeSurcharge?: boolean;
  promoCodeSelectionType?: keyof typeof CheckOutPromoCodeSelectionType;
};

const PurchasePaymentFormInputs: FC<IProps> = ({
  payDetails,
  handleDataChange,
  paymentSectionData,
  countriesWithState,
  onPayClick,
  color,
  merchantAccount,
  items,
  addons,
  action,
  hasMultiplePaymentMethods,
  isPaymentInProgress,
  seller,
  showSurchargeCheckbox,
  includeSurcharge,
  promoCodeSelectionType,
}) => {
  const billingAddressRef = React.useRef<HTMLDivElement>(null);
  const shippingAddressRef = React.useRef<HTMLDivElement>(null);
  const payButtonRef = React.useRef<HTMLButtonElement>(null);
  const formInputsRef = React.useRef<HTMLDivElement>(null);
  const { formData } = paymentSectionData;
  const { formFields } = payDetails.formData;

  useLayoutEffect(() => {
    if (!action) return;
    if (action === "isBillingAddressChecked") {
      if (
        payDetails.formData.isBillingAddressChecked &&
        billingAddressRef.current
      ) {
        billingAddressRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
    if (action === "isShippingAddressChecked") {
      if (
        payDetails.formData.isShippingAddressChecked &&
        shippingAddressRef.current
      ) {
        shippingAddressRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
    if (action === "payButtonType") {
      payButtonRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    if (action === "formFields") {
      formInputsRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [action]);

  const handleFormInputChange = (field: string, value: string) => {
    handleDataChange("formData", { ...formData, [field]: value });
  };

  const handleSelect = (id: string | null) => {
    if (id) {
      const country = countriesWithState.find((country) => country.id === id);
      if (country) {
        handleDataChange("countryDetails", country);
      }
    }
  };

  const renderField = (field: CheckOutPayPageFormFields) => {
    let placeholder = field.field;

    switch (field.field) {
      case CheckOutPayFields.FirstLastName:
        return (
          <>
            <FlexContainer
              alignItems="center"
              spacing={10}
              key={field.field}
              ref={formInputsRef}
            >
              <Input
                type="text"
                id="firstName"
                value={formData["firstName"] || ""}
                onChange={(e) =>
                  handleFormInputChange("firstName", e.target.value)
                }
                placeholder="First Name"
                error={paymentSectionData.hasError(
                  "firstName" as keyof PurchasePaymentModel
                )}
                errorMessage="*Please enter first and last name"
              />
              <Input
                type="text"
                id="lastName"
                value={formData["lastName"] || ""}
                onChange={(e) =>
                  handleFormInputChange("lastName", e.target.value)
                }
                placeholder="Last Name"
                error={paymentSectionData.hasError(
                  "lastName" as keyof PurchasePaymentModel
                )}
                errorMessage="*Please enter first and last name"
              />
            </FlexContainer>
          </>
        );
      case CheckOutPayFields.MobileNumber:
        return (
          <FlexContainer fxLayout="column">
            <CountryPhoneDropdown
              key={field.field}
              selectedCountry={paymentSectionData.countryDetails}
              error={paymentSectionData.hasError(
                "MobileNumber" as keyof PurchasePaymentModel
              )}
              errorMessage={"*Please enter a valid mobile number"}
              onCountryChange={(countryId: string) => handleSelect(countryId)}
              phoneNumber={formData[field.field] || ""}
              onPhoneNumberChange={(value: string) =>
                handleFormInputChange(field.field, value)
              }
              availableCountries={countriesWithState}
              className="purchase-phone-dropdown"
            />
          </FlexContainer>
        );
      case CheckOutPayFields.EmailAddress:
        placeholder = "Email";
        break;
      default:
        placeholder = field.field === "TaxId" ? "Tax Id" : field.field;
    }

    return (
      <FlexContainer key={field.field} fxLayout="column">
        <Input
          type="text"
          id={field.field}
          value={formData[field.field] || ""}
          onChange={(e) => handleFormInputChange(field.field, e.target.value)}
          placeholder={placeholder}
          error={paymentSectionData.hasError(
            field.field as keyof PurchasePaymentModel
          )}
          errorMessage="*Please enter a valid value"
        />
      </FlexContainer>
    );
  };

  const calculateTotalPrice = (
    items: CustomerFacingCheckOutItemModel[],
    addOns: PurchaseCheckOutAddOnDetails[],
    surchargePercentage?: number
  ): number => {
    const clonedItems = items.map(
      (item) =>
        new CustomerFacingCheckOutItemModel({
          ...item,
          selectedVariantIndex:
            item.selectedVariantIndex === -1 ? 0 : item.selectedVariantIndex,
        })
    );

    const purchaseDetailsToApplyPromoCode: PurchaseItemsPriceModel[] =
      getPurchaseItemsPriceDetails(clonedItems, addOns, 0, undefined);

    const totalPriceToApplyPromoCode = purchaseDetailsToApplyPromoCode
      .filter((priceDetail) => priceDetail.dueLater !== true)
      .reduce((total, priceDetail) => {
        return total + priceDetail.totalPrice;
      }, 0);

    const minimumPurchase = paymentSectionData.promotionRelatedData
      ? paymentSectionData.promotionRelatedData.minimumPurchase || 0
      : 0;

    const promoData =
      promoCodeSelectionType === "ApplyCode"
        ? totalPriceToApplyPromoCode > minimumPurchase
          ? paymentSectionData.promotionRelatedData
          : undefined
        : paymentSectionData.promotionRelatedData;

    const purchaseDetails: PurchaseItemsPriceModel[] =
      getPurchaseItemsPriceDetails(
        clonedItems,
        addOns,
        surchargePercentage,
        promoData || undefined
      );
    const totalPrice = purchaseDetails
      .filter((priceDetail) => priceDetail.dueLater !== true)
      .reduce((total, priceDetail) => {
        return total + priceDetail.totalPrice;
      }, 0);
    return totalPrice;
  };

  const includeSurchargeOnApplyPromo = paymentSectionData.promotionRelatedData
    ? !paymentSectionData.promotionRelatedData.waiveSurcharge
    : true;

  const price = calculateTotalPrice(
    items,
    addons,
    includeSurcharge && includeSurchargeOnApplyPromo
      ? merchantAccount.surchargePercentage()
      : 0
  ).toFixed(2);

  const renderPayButton = () => {
    const value =
      payDetails.formData.payButtonType === CheckOutPayButtonCaption.Custom
        ? payDetails.formData.buttonValue
        : CheckOutPayButtonCaption[payDetails.formData.payButtonType];

    const isButtonDisabled = paymentSectionData.paymentMethodId === "";

    const handlePayClick = () => {
      const isValid = paymentSectionData.validate();

      if (!isValid) {
        handleDataChange("formData", { ...formData });
        return;
      }
      onPayClick();
    };

    return (
      <CustomButton
        id="pay-button"
        className="pay-button payment-main-btn"
        disabled={isButtonDisabled || isPaymentInProgress}
        bgColor={color?.primary?.rgba}
        pillShape={color?.buttonStyleType === CheckOutButtonStyle.Round}
        roundedRect={
          color?.buttonStyleType === CheckOutButtonStyle.SquareRoundCorner
        }
        onClick={() => handlePayClick()}
        ref={payButtonRef}
      >
        {price === "0.00" ? "Place Order" : `${value} $${price}`}{" "}
      </CustomButton>
    );
  };

  const handleAddressChange = (
    addressType: keyof PurchasePaymentModel,
    field: keyof AddressModel,
    value: string | CountryWithStateModel | StateModel
  ) => {
    if (addressType === "billingAddress" || addressType === "shippingAddress") {
      const updatedAddress = new AddressModel({
        ...paymentSectionData[addressType],
        [field]: value,
      });
      handleDataChange(addressType, updatedAddress);
    }
  };

  const handleShipping = (key: keyof PurchasePaymentModel, value: boolean) => {
    handleDataChange(key, value);
  };

  const checkBoxInput = (
    key: keyof PurchasePaymentModel,
    value: boolean,
    label: string,
    className?: string
  ) => {
    return (
      <Input
        type="checkbox"
        value={value}
        onChange={(e) => handleShipping(key, e.target.checked)}
        label={label}
        className={className}
      />
    );
  };
  return (
    <FlexContainer
      fxLayout="column"
      spacing={15}
      className={`payment-form-input ${hasMultiplePaymentMethods ? "multiple-payment-method-form" : "default-payment-method-form"} `}
    >
      {formFields.map((field: CheckOutPayPageFormFields) => renderField(field))}
      {/* {payDetails.allowTaxId && (
        <>
          <Input
            type="text"
            id="taxId"
            value={paymentSectionData.taxId}
            onChange={(e) => handleDataChange("taxId", e.target.value)}
            placeholder="Enter Tax Id"
            error={paymentSectionData.hasError(
              "taxId" as keyof PurchasePaymentModel
            )}
            errorMessage="*Please enter a valid tax id"
          />
        </>
      )} */}
      {payDetails.formData.isBillingAddressChecked && (
        <div ref={billingAddressRef} id="billingAddressSection">
          <FlexContainer fxLayout="column" spacing={15}>
            <CustomLabel className="sub-heading">Billing Address</CustomLabel>
            <AddressFormComponent
              address={paymentSectionData.billingAddress}
              countriesWithState={countriesWithState}
              onAddressChange={(
                field: keyof AddressModel,
                value: string | CountryWithStateModel | StateModel
              ) => handleAddressChange("billingAddress", field, value)}
            />
          </FlexContainer>
        </div>
      )}
      {payDetails.formData.isShippingAddressChecked && (
        <div ref={shippingAddressRef} id="shippingAddressSection">
          <FlexContainer fxLayout="column" spacing={15}>
            <div className="full-width">
              <FlexContainer alignItems="center" justifyContent="space-between">
                <CustomLabel className="sub-heading" margin="10px 0px 0px">
                  Shipping Address
                </CustomLabel>
                {payDetails.formData.isBillingAddressChecked &&
                  checkBoxInput(
                    "shippingSameAsBilling",
                    paymentSectionData.shippingSameAsBilling,
                    "Same as Billing",
                    "same-as-billing"
                  )}
              </FlexContainer>
            </div>
            {!paymentSectionData.shippingSameAsBilling && (
              <AddressFormComponent
                address={paymentSectionData.shippingAddress}
                countriesWithState={countriesWithState}
                onAddressChange={(
                  field: keyof AddressModel,
                  value: string | CountryWithStateModel | StateModel
                ) => handleAddressChange("shippingAddress", field, value)}
              />
            )}
          </FlexContainer>
        </div>
      )}
      <div>
        <CustomLabel
          className="sub-heading"
          margin={
            !paymentSectionData.shippingSameAsBilling
              ? "20px 0px 0px"
              : "10px 0px 0px"
          }
        >
          Terms & Conditions
        </CustomLabel>
      </div>
      {showSurchargeCheckbox && (
        <div>
          <FlexContainer fxLayout="column" spacing={2}>
            <Input
              type="checkbox"
              id="surcharge"
              value={paymentSectionData.surchargeChecked}
              onChange={(e) => {
                handleDataChange("surchargeChecked", e.target.checked);
              }}
              label="Cover payment processing costs"
              className="terms-checkbox"
              error={paymentSectionData.hasError(
                "surchargeChecked" as keyof PurchasePaymentModel
              )}
              errorMessage="*Please click this checkbox to cover payment processing costs"
            />
            <CustomLabel>{payDetails.surchargeText}</CustomLabel>
          </FlexContainer>
        </div>
      )}
      {false && (
        <div>
          <FlexContainer fxLayout="column" spacing={2}>
            <Input
              type="checkbox"
              id="updates"
              onChange={(e) => {
                handleDataChange("updatesOffers", e.target.checked);
              }}
              value={paymentSectionData.updatesOffers}
              label="Get updates and special offers"
              className="terms-checkbox"
              error={paymentSectionData.hasError(
                "updatesOffers" as keyof PurchasePaymentModel
              )}
              errorMessage="*Please select updates and special offers"
            />
            <CustomLabel>
              I agree to receive text and/or email communication. I may opt-out
              at any time. For text messages, message and data rates may apply
              and I may opt-out by replying STOP if texted.
            </CustomLabel>
          </FlexContainer>
        </div>
      )}
      {false && (
        <div>
          <FlexContainer fxLayout="column" spacing={2}>
            <Input
              type="checkbox"
              id="savePayment"
              value={paymentSectionData.savePaymentMethod}
              onChange={(e) => {
                handleDataChange("savePaymentMethod", e.target.checked);
              }}
              label="Save payment method for future purchases"
              className="terms-checkbox"
              error={paymentSectionData.hasError(
                "savePaymentMethod" as keyof PurchasePaymentModel
              )}
              errorMessage="*Please select save payment methods"
            />
            <CustomLabel>
              We will send a text code to your mobile phone to confirm.
            </CustomLabel>
          </FlexContainer>
        </div>
      )}
      {payDetails.sellerTermsAndConditions !== "DoNotDisplay" && (
        <div>
          <FlexContainer fxLayout="column" spacing={2}>
            <Input
              type="checkbox"
              id="terms"
              value={paymentSectionData.acceptTermsAndConditions}
              onChange={(e) => {
                handleDataChange("acceptTermsAndConditions", e.target.checked);
              }}
              label="I accept terms and conditions"
              className="terms-checkbox"
              error={paymentSectionData.hasError(
                "acceptTermsAndConditions" as keyof PurchasePaymentModel
              )}
              errorMessage="*Please accept terms and conditions"
            />
            <CustomLabel>
              By purchasing today, you allow{" "}
              <strong>{seller?.name || ""}</strong> to charge your credit card{" "}
              <strong>${price}</strong> now and any future payments in
              accordance with their terms and conditions. You can cancel future
              payments on this credit card at any time. Changing your method of
              payment in the future may still result in an amount due charged
              for any active subscriptions unless cancelled.
            </CustomLabel>
          </FlexContainer>
        </div>
      )}
      {renderPayButton()}
    </FlexContainer>
  );
};

export default observer(PurchasePaymentFormInputs);